import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
} from '@mui/material'
import {
  Dashboard,
  ShoppingCart,
  People,
  AccountBalance,
} from '@mui/icons-material'

interface NavigationItem {
  label: string
  value: string
  icon: React.ReactElement
  path: string
}

const navigationItems: NavigationItem[] = [
  {
    label: 'Dashboard',
    value: 'dashboard',
    icon: <Dashboard />,
    path: '/dashboard',
  },
  {
    label: 'Orders',
    value: 'orders',
    icon: <ShoppingCart />,
    path: '/orders',
  },
  {
    label: 'Employees',
    value: 'employees',
    icon: <People />,
    path: '/employees',
  },
  {
    label: 'Payroll',
    value: 'payroll',
    icon: <AccountBalance />,
    path: '/payroll',
  },
]

export const BottomNavigation: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  // Determine active navigation item based on current path
  const getCurrentValue = () => {
    const path = location.pathname
    const item = navigationItems.find((item) => path.startsWith(item.path))
    return item?.value || 'dashboard'
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const item = navigationItems.find((item) => item.value === newValue)
    if (item) {
      navigate(item.path)
    }
  }

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.08)',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
      }}
      elevation={0}
    >
      <MuiBottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          backgroundColor: 'transparent',
          height: 70,
          px: 1,
          py: 1,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '8px 12px',
            color: 'text.secondary',
            borderRadius: '16px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '16px',
              background: 'transparent',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '&.Mui-selected': {
              color: '#667eea',
              '&::before': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              },
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.1)',
                filter: 'drop-shadow(0 2px 4px rgba(102, 126, 234, 0.3))',
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#667eea',
                '&.Mui-selected': {
                  fontSize: '0.75rem',
                },
              },
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              fontWeight: 500,
              marginTop: '6px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&.Mui-selected': {
                fontSize: '0.75rem',
              },
            },
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.6rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={item.icon}
          />
        ))}
      </MuiBottomNavigation>
    </Paper>
  )
}
